import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFinestraIndietro } from '../utils/finestraIndietro';
import { confermaUscita } from '../utils/uscitaProtetta';
import useAzione from '../navigazione/useAzione';
import useDalNumero from '../navigazione/dalNumero';
import {
    Plus, Search, X, FileText, Printer, Check, Trash2, Pencil,
    ChevronLeft, ChevronRight, Building2, User, RotateCcw, Send,
} from 'lucide-react';
import api from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import StatoSdi, { DA_RIGENERARE, STATI_DOPO_SCARTO, rigeneraXml, leggiXml, segnaComeTrasmessa } from '../components/fatturazione/StatoSdi';
import ScontoTotale from '../components/fatturazione/ScontoTotale';
import NonFatturate from '../components/fatturazione/NonFatturate';
import SegnoOpposizioneTs from '../components/SegnoOpposizioneTs';
import AvvisoCfVecchio from '../components/fatturazione/AvvisoCfVecchio';
import { notificaErrore } from '../utils/notifiche';
import { dataIta, momentoItaliano } from '../utils/oraItaliana';
import { useTogliBozzaVuota } from '../utils/bozzaVuota';
import { ModaleStorno, NuovoDocumento, RigheDocumento } from './Fatturazione';
import { FILTRO_DA_CORREGGERE, MESE_TUTTI } from '../utils/fattureDaCorreggere';
import '../styles/FatturazioneConsole.css';

/**
 * FatturazioneConsole — Fatture nello stile «Console» (mockup «Fatture», Alex
 * 2/10/2026; la voce del menu e' Fatture, il percorso resta /fatturazione).
 * E' la pagina dell'utenza con l'interruttore acceso: con lo stile di prima
 * si disegna Fatturazione.jsx (FatturazionePagina sceglie). Le finestre
 * (nuovo documento, bozza ripresa, nota di credito) sono le stesse: si
 * importano da Fatturazione.jsx, e ne cambia solo il foglio
 * (FatturazioneConsole.css, sotto .stile-console).
 *
 * Le decisioni di Alex del 13/10/2026: F1 un solo filtro «Da correggere»; F2
 * «Correggi e rifai l'XML» finche' non c'e' Aruba; F3 l'«Emesso» resta col
 * totale delle fatture e le note di credito dette accanto; F4 le bozze nel
 * mese di oggi; F5 il filtro «Bozze» e nient'altro dei vecchi stati; F6 il
 * trimestre; F7 solo i mesi con documenti.
 *
 * Finora le fatture nascevano solo dalla scheda paziente o da una
 * visita: qui la segreteria può emetterne una da zero, che serve per
 * tutto ciò che non passa da una prestazione registrata.
 *
 * L'elenco è paginato lato server: con ventimila documenti l'anno
 * caricarli tutti non è un'opzione.
 *
 * QUELLO CHE SI VEDE LO DICE IL SERVER. I tre riquadri del mese, i numeri
 * accanto ai filtri, lo stato del Sistema TS e dell'incasso di ogni riga e
 * lo scarto dello SdI arrivano dalla stessa lettura dell'elenco
 * (GET /fatturazione con riepilogo=1): nessuna somma e nessuna regola qui,
 * e nessuna lettura in piu' all'apertura - la pagina lascia traccia nel
 * registro degli accessi (PAGINE_CON_TRACCIA), e quello che legge
 * all'apertura e' contato dallo scenario 15.
 */

/* Gli importi nella tabella senza il simbolo (la colonna dice cosa sono),
   nei riquadri col simbolo. */
const importo = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
}));
const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
}));

/* Le date in ora italiana, dall'unica fonte della schermata
   (utils/oraItaliana): il fuso lo decide la clinica, non il computer. Col
   mese scelto l'anno lo dice gia' il mese. */
const dataDoc = (d, conAnno) => {
    if (!d) return '—';
    const t = dataIta(d);
    return conAnno ? t : t.slice(0, 5);
};
/* La data intera, per le finestre (le visite di medicina del lavoro). */
const data = (d) => dataDoc(d, true);
/* Il momento di una notifica dello SdI. */
const momento = (t) => {
    if (!t) return null;
    const m = momentoItaliano(t);
    return `${m.data} alle ${m.ora}`;
};

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio',
    'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const nomeMese = (iso) => {
    const m = /^(\d{4})-(\d{2})$/.exec(String(iso || ''));
    return m ? `${MESI[Number(m[2]) - 1]} ${m[1]}` : '';
};
const maiuscola = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
/* I PERIODI DA OFFRIRE (Alex, 13/10/2026: F7 solo i mesi in cui c'e' un
   documento, F6 il trimestre). Li dice il server (periodo.mesi,
   periodo.trimestri): qui si dispongono e si danno un nome. Il mese di oggi
   c'e' sempre ed e' il primo (e' dove si apre la pagina e dove stanno le
   bozze); il periodo scelto c'e' anche se il server non lo elenca (un
   indirizzo salvato, «Mostrali»). Del giorno di oggi non si sa niente qui:
   «AAAA-MM» e «AAAA-Tn» sono nomi, e quale e' il corrente lo dice il
   database. */
const nomeTrimestre = (iso) => {
    const m = /^(\d{4})-T([1-4])$/.exec(String(iso || ''));
    return m ? `${m[2]}° trimestre ${m[1]}` : '';
};
const MESI_DEL_TRIMESTRE = ['gen–mar', 'apr–giu', 'lug–set', 'ott–dic'];
const intervalloTrimestre = (iso) => {
    const m = /^\d{4}-T([1-4])$/.exec(String(iso || ''));
    return m ? MESI_DEL_TRIMESTRE[Number(m[1]) - 1] : '';
};
const elencoMesi = (corrente, elencati, scelto) => {
    if (!/^\d{4}-\d{2}$/.test(corrente || '')) return scelto ? [scelto] : [];
    const fuori = [corrente, ...(elencati || []).filter((x) => x !== corrente)];
    if (scelto && !fuori.includes(scelto)) fuori.push(scelto);
    return fuori.sort().reverse();
};
const elencoTrimestri = (corrente, elencati, scelto) => {
    if (!/^\d{4}-T[1-4]$/.test(corrente || '')) return scelto ? [scelto] : [];
    const fuori = [corrente, ...(elencati || []).filter((x) => x !== corrente)];
    if (scelto && !fuori.includes(scelto)) fuori.push(scelto);
    return fuori.sort().reverse();
};

/* I filtri, coi numeri del server (conteggi). Ognuno e' una condizione che
   il gestionale ha gia' (la scrive fatturazioneController, VISTE): qui c'e'
   solo come si chiamano. «Da correggere» conta come il numero accanto alla
   voce Fatture del menu: XML da correggere e documenti scartati dallo SdI.
   L'ordine e' quello del mockup, dove «Da correggere» sta al posto di
   «Scartate dallo SdI»: conta anche l'XML che non ha passato i controlli,
   come il numero del menu. */
const VISTE = [
    { chiave: '', conteggio: 'tutte', etichetta: 'Tutte' },
    { chiave: 'da_inviare_ts', conteggio: 'da_inviare_ts', etichetta: 'Da inviare al TS',
      titolo: 'Documenti emessi con righe per il Sistema TS non ancora trasmesse' },
    /* La chiave e' anche nell'indirizzo del numero accanto alla voce Fatture
       (utils/fattureDaCorreggere.js): una costante sola. */
    { chiave: FILTRO_DA_CORREGGERE, conteggio: 'da_correggere', etichetta: 'Da correggere',
      titolo: 'XML da correggere o scartati dallo SdI' },
    { chiave: 'in_attesa_sdi', conteggio: 'in_attesa_sdi', etichetta: 'In attesa di esito',
      titolo: 'Inviati allo SdI, senza ancora un esito' },
    { chiave: 'non_incassate', conteggio: 'non_incassate', etichetta: 'Non incassate',
      titolo: 'Fatture emesse con qualcosa ancora da incassare' },
    /* F5 (Alex, 13/10/2026): dei vecchi stati torna solo «Bozze». Stanno nel
       mese di oggi (F4), come l'elenco le ha sempre messe in cima. */
    { chiave: 'bozze', conteggio: 'bozze', etichetta: 'Bozze',
      titolo: 'Documenti non ancora emessi, nel periodo che contiene oggi' },
];
const CHIAVI_VISTE = VISTE.map((v) => v.chiave);

/* Il Sistema TS di un documento, come lo dice il server (ts_stato). */
const TS = {
    inviata:           { testo: 'Inviata', classe: 'fat-ts--ok fat-ts-ok' },
    da_inviare:        { testo: 'Da inviare', classe: 'fat-ts--da-fare' },
    partita_iva:       { testo: 'Esclusa · P.IVA', classe: 'fat-ts--spenta',
                         titolo: 'Chi ha partita IVA va allo SdI e non al Sistema TS' },
    senza_cf_italiano: { testo: 'Esclusa · senza CF italiano', classe: 'fat-ts--spenta',
                         titolo: 'Chi non ha un codice fiscale italiano non va al Sistema TS' },
    nessuna_riga:      { testo: 'Non va al TS', classe: 'fat-ts--spenta',
                         titolo: 'Nessuna riga del documento va al Sistema TS' },
};

const FatturazioneConsole = () => {
    const navigate = useNavigate();
    /* LA RICERCA: il testo nel campo, e quello che parte, un momento dopo
       l'ultima lettera. Una lettura per lettera e una risposta vecchia che
       arriva dopo una nuova mostravano l'elenco di una parola gia'
       cancellata. */
    const [cerca, setCerca] = useState('');
    const [q, setQ] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setQ(cerca.trim()), 250);
        return () => clearTimeout(t);
    }, [cerca]);
    /* IL NUMERO ACCANTO ALLA VOCE FATTURE TOGLIE LA RICERCA, anche con la
       pagina gia' aperta: le righe sono quelle che il numero conta. La regola
       di Alex e' per «Da completare» di Pazienti (7/10/2026, risposta B7:
       «clic su "Da completare" con una ricerca accesa toglie la ricerca:
       si'»); qui vale per coerenza, scelta del beta: il numero di Fatture
       promette un elenco come quello di Pazienti (B6). Solo il numero: i
       pulsanti dei filtri della pagina e «Mostrali» tengono la ricerca,
       perche' «Da correggere» qui e' uno dei filtri dell'elenco, da usare
       anche insieme a una ricerca. Tutte e due, il campo e la ricerca che
       parte: nel disegno, quindi la lettura parte una volta e gia' senza
       (navigazione/dalNumero.js). */
    const dalNumero = useDalNumero();
    if (dalNumero && (cerca !== '' || q !== '')) {
        setCerca('');
        setQ('');
    }
    const [elenco, setElenco] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [nuova, setNuova] = useState(false);
    /* Le azioni della barra comandi (Ctrl K) e di «Nuovo»: lo stesso gesto
       del pulsante della pagina (navigazione/useAzione.js). */
    useAzione({ 'nuovo-documento': () => setNuova(true) });
    const [ripresa, setRipresa] = useState(null);
    const [storno, setStorno] = useState(null);   // { fattura, righe }
    const [rifacendo, setRifacendo] = useState(null);   // id del documento di cui si rifa' l'XML
    const [segnando, setSegnando] = useState(null);     // id del documento che si segna come trasmesso
    /* IL DOCUMENTO APERTO STA NELL'INDIRIZZO (?documento=…, Alex 28/09/2026):
       «Indietro» lo chiude e torna all'elenco, ricaricare lo riapre. Le
       altre due finestre si chiudono con «Indietro» senza un indirizzo.
       Nell'indirizzo stanno anche il mese (?mese=AAAA-MM, o «tutti») e il
       filtro (?filtro=…), scritti sostituendo l'indirizzo, senza un passo di
       cronologia: una ricarica - anche quella della versione nuova - li
       ritrova. Il mese di oggi non si scrive, come il giorno dell'agenda:
       una pagina ricaricata il mese dopo apre il mese nuovo. */
    const [parametri, setParametri] = useSearchParams();
    const documentoAperto = parametri.get('documento');
    /* Il periodo e' un mese (?mese=AAAA-MM), un trimestre (?trimestre=AAAA-Tn)
       o tutti i mesi (?mese=tutti); niente = il mese di oggi. Un valore che non
       ha la forma di uno dei tre vale come niente: l'indirizzo e' di chi lo
       scrive. */
    const trimestreScelto = /^\d{4}-T[1-4]$/.test(parametri.get('trimestre') || '') ? parametri.get('trimestre') : '';
    const meseGrezzo = trimestreScelto ? '' : (parametri.get('mese') || '');
    const meseScelto = (meseGrezzo === MESE_TUTTI || /^\d{4}-(0[1-9]|1[0-2])$/.test(meseGrezzo)) ? meseGrezzo : '';
    const filtroScelto = CHIAVI_VISTE.includes(parametri.get('filtro') || '') ? (parametri.get('filtro') || '') : '';
    const apriDocumento = (id) => setParametri((p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('documento', String(id)); else n.delete('documento');
        return n;
    });
    /* LA PAGINA DELL'ELENCO VALE PER UNA RICERCA, UN MESE E UN FILTRO, e
       cambiato uno dei tre si riparte dalla prima. Non con un setPagina(1)
       accanto a ogni gesto: mese e filtro stanno nell'indirizzo, e
       l'indirizzo lo cambiano anche «Mostrali», il promemoria dello scarto
       (navigate, a pagina gia' montata) e la voce del menu. Con la pagina
       tenuta a parte, chi era alla 2 di un mese e apriva il promemoria
       chiedeva la 2 di «Da correggere», che ne ha 8: zero righe sotto «8
       documenti» e nessun pulsante per tornare indietro (revisione del
       6/10/2026). Qui la pagina ricorda per quale elenco e' stata scelta,
       e per un elenco diverso vale 1: nessuna lettura in piu'. E la si
       dimentica appena l'elenco cambia (lo stato si riscrive durante il
       disegno, il modo di React per «quello che c'era prima»): tenuta,
       tornando a «Tutte» dopo il promemoria si ritrovava la pagina 2. */
    const elencoScelto = JSON.stringify([q, meseScelto, trimestreScelto, filtroScelto]);
    const [paginaScelta, setPaginaScelta] = useState({ elenco: elencoScelto, n: 1 });
    if (paginaScelta.elenco !== elencoScelto) setPaginaScelta({ elenco: elencoScelto, n: 1 });
    const pagina = paginaScelta.elenco === elencoScelto ? paginaScelta.n : 1;
    const vaiAPagina = (n) => setPaginaScelta({ elenco: elencoScelto, n: Math.max(n, 1) });
    const scegli = (chiave, valore) => {
        setParametri((p) => {
            const n = new URLSearchParams(p);
            if (valore) n.set(chiave, valore); else n.delete(chiave);
            return n;
        }, { replace: true });
    };
    useFinestraIndietro(nuova, () => { if (!confermaUscita()) return false; setNuova(false); return true; });
    useFinestraIndietro(!!storno, () => { if (!confermaUscita()) return false; setStorno(null); return true; });

    /* L'errore della lettura dell'elenco sta per conto suo, con «Riprova»:
       la lettura riuscita lo toglie, quelli dei gesti restano in `errore`
       (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);
    /* Solo l'ultima lettura chiesta arriva a schermo. */
    const ultima = useRef(0);
    const carica = useCallback(async () => {
        const io = ++ultima.current;
        try {
            setCaricamento(true);
            const params = { pagina, per_pagina: 50, riepilogo: 1 };
            if (trimestreScelto) params.trimestre = trimestreScelto;
            else params.mese = meseScelto === MESE_TUTTI ? undefined : (meseScelto || 'corrente');
            if (filtroScelto) params.filtro = filtroScelto;
            if (q) params.q = q;
            const r = await conUnTentativo(() => api.get('/fatturazione', { params }));
            if (io !== ultima.current) return;
            setElenco(r.data.data);
            setErrore(null);
            setErroreLettura(null);
        } catch (e) {
            if (io !== ultima.current) return;
            setErroreLettura(motivoDellaLettura(e, 'Non riesco a caricare i documenti'));
        } finally {
            if (io === ultima.current) setCaricamento(false);
        }
    }, [q, meseScelto, trimestreScelto, filtroScelto, pagina]);

    useEffect(() => { carica(); }, [carica]);

    const scaricaPdf = async (f) => {
        try {
            const r = await api.get(`/fatturazione/${f.id}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            setErrore('Non riesco a produrre il PDF');
        }
    };

    /* «Vedi XML» dello scarto: l'ultimo XML valido, quello che lo SdI ha
       scartato, in una scheda nuova. La rotta e' quella di «Scarica XML» (e
       resta nel registro degli accessi come lei), al gesto. */
    const vediXml = async (f) => {
        try {
            const { blob } = await leggiXml(f);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            notificaErrore(e.message);
        }
    };
    /* «Correggi e rifai l'XML» (e «Rigenera l'XML» per un XML che non ha
       passato i controlli): la rotta di sempre, che rifa' l'XML dopo la
       correzione. L'etichetta del mockup era «Correggi e reinvia», pensata per
       Aruba; finche' Aruba non c'e' (provider manuale) niente parte da qui,
       e il pulsante non deve dire che parte (F2, Alex, 13/10/2026).
       Solo dove la regola di oggi lo permette (DA_RIGENERARE, la lista di
       StatoSdi); il resto lo decide il server. */
    const rifaiXml = async (f) => {
        setRifacendo(f.id);
        try {
            if (await rigeneraXml(f)) await carica();
        } finally {
            setRifacendo(null);
        }
    };

    /* «Segna come trasmessa» dalla riga aperta dello scarto corretto: la
       funzione di StatoSdi, una sola (domanda 30, migration 335). */
    const segnaTrasmessa = async (f) => {
        setSegnando(f.id);
        try {
            if (await segnaComeTrasmessa(f)) await carica();
        } finally {
            setSegnando(null);
        }
    };

    const riprendi = (f) => apriDocumento(f.id);
    useEffect(() => {
        if (!documentoAperto) { setRipresa(null); return; }
        let vivo = true;
        api.get(`/fatturazione/${documentoAperto}`)
            .then((r) => { if (vivo) setRipresa(r.data.data); })
            .catch(() => { if (vivo) { setErrore('Non riesco a riaprire la bozza'); apriDocumento(null); } });
        return () => { vivo = false; };
        // eslint-disable-next-line
    }, [documentoAperto]);
    const chiudiRipresa = () => { if (confermaUscita()) apriDocumento(null); };

    /* Lo storno si faceva solo dalla scheda del paziente: chi partiva da
       qui doveva prima cercare il paziente, e su una fattura intestata a
       un'azienda il paziente non c'e' nemmeno. */
    const apriStorno = async (f) => {
        try {
            setErrore(null);
            const r = await api.get(`/fatturazione/${f.id}`);
            setStorno(r.data.data);
        } catch (e) {
            setErrore('Non riesco ad aprire il documento da stornare');
        }
    };

    const eliminaBozza = async (f) => {
        if (!window.confirm('Eliminare questa bozza? Non è recuperabile.')) return;
        try {
            await api.delete(`/fatturazione/${f.id}`);
            carica();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a eliminare la bozza');
        }
    };

    const periodo = elenco?.periodo || {};
    const conteggi = elenco?.conteggi || {};
    const riepilogo = elenco?.riepilogo || null;
    const fuori = elenco?.fuori_periodo?.da_correggere || 0;
    /* tutti i mesi = nessun periodo. */
    const conPeriodo = meseScelto !== MESE_TUTTI;
    const mesi = elencoMesi(periodo.mese_corrente, periodo.mesi,
        /^\d{4}-\d{2}$/.test(meseScelto) ? meseScelto : null);
    const trimestri = elencoTrimestri(periodo.trimestre_corrente, periodo.trimestri, trimestreScelto || null);
    /* Il periodo nei riquadri: «a ottobre» / «nel 3° trimestre», con l'anno
       solo se non e' quello di oggi. */
    const annoDiOggi = String(periodo.mese_corrente || '').slice(0, 4);
    const periodoInBreve = (() => {
        if (periodo.trimestre) {
            const [anno, tr] = periodo.trimestre.split('-T');
            return `nel ${tr}° trimestre${anno === annoDiOggi ? '' : ` ${anno}`}`;
        }
        const m = periodo.mese;
        if (!m) return '';
        const [nome, anno] = nomeMese(m).split(' ');
        return `a ${anno === annoDiOggi ? nome : `${nome} ${anno}`}`;
    })();
    /* Il periodo per esteso, nei messaggi di elenco vuoto. */
    const periodoPerEsteso = periodo.trimestre ? `nel ${nomeTrimestre(periodo.trimestre)}`
        : (periodo.mese ? `a ${nomeMese(periodo.mese)}` : '');
    const valorePeriodo = trimestreScelto ? `T:${trimestreScelto}`
        : (meseScelto === MESE_TUTTI ? MESE_TUTTI
            : (meseScelto && meseScelto !== periodo.mese_corrente ? meseScelto : ''));
    /* Il mese e il trimestre stanno in due parametri dell'indirizzo, e uno
       toglie l'altro. Il mese di oggi non si scrive. */
    const scegliPeriodo = (valore) => setParametri((p) => {
        const n = new URLSearchParams(p);
        n.delete('mese');
        n.delete('trimestre');
        if (valore.startsWith('T:')) n.set('trimestre', valore.slice(2));
        else if (valore) n.set('mese', valore);
        return n;
    }, { replace: true });

    return (
        <div className="fat-wrap" role="region" aria-label="Fatture">
            {/* Niente titolo a vista: lo dice il percorso in alto,
                «Amministrazione / Fatture». Per i lettori di schermo la pagina
                e' una regione col suo nome, e i campi hanno il nome
                nell'attributo (un testo nascosto con clip e' un testo
                «tagliato» per provaRifinitura). */}

            <div className="fat-barra" data-non-trattiene>
                <label className="fat-cerca">
                    <Search size={15} aria-hidden="true" />
                    <input
                        type="search"
                        aria-label="Cerca un documento"
                        value={cerca}
                        onChange={(e) => setCerca(e.target.value)}
                        placeholder="Numero, intestatario o codice fiscale…"
                    />
                </label>
                <select className="fat-mese" aria-label="Periodo" value={valorePeriodo}
                        onChange={(e) => scegliPeriodo(e.target.value)}>
                    <optgroup label="Mesi">
                        {mesi.map((m) => (
                            <option key={m} value={m === periodo.mese_corrente ? '' : m}>{maiuscola(nomeMese(m))}</option>
                        ))}
                        {!mesi.length && <option value="">Questo mese</option>}
                    </optgroup>
                    {trimestri.length > 0 && (
                        <optgroup label="Trimestri">
                            {trimestri.map((x) => (
                                <option key={x} value={`T:${x}`}>{nomeTrimestre(x)} ({intervalloTrimestre(x)})</option>
                            ))}
                        </optgroup>
                    )}
                    <option value={MESE_TUTTI}>Tutti i mesi</option>
                </select>
                {/* L'invio al Sistema TS e' la coda di questo ciclo: si
                    fattura, e poi si manda. Stessi ruoli di questa
                    schermata - admin e segreteria - quindi chi la vede puo'
                    anche aprirlo. */}
                <button className="fat-btn" onClick={() => navigate('/sistema-ts')}
                        title="Invio delle spese sanitarie al Sistema TS">
                    <Send size={15} aria-hidden="true" /> Sistema TS
                </button>
                <button className="fat-btn primario" onClick={() => setNuova(true)}>
                    <Plus size={15} aria-hidden="true" /> Nuovo documento
                </button>
            </div>

            <NonFatturate />

            <ErroreLettura testo={erroreLettura} onRiprova={carica} classe="fat-errore" Contenitore="div" />
            {errore && <div className="fat-errore" role="alert">{errore}</div>}

            {riepilogo && (
                <section className="fat-riquadri" aria-label="Il periodo in cifre">
                    <div className="fat-riquadro">
                        <span className="fat-riquadro-nome">
                            {conPeriodo ? `Emesso ${periodoInBreve}` : 'Emesso'}
                        </span>
                        <span className="fat-riquadro-valore rm-numeri">{euro(riepilogo.emesso)}</span>
                        {/* Le note di credito non si tolgono dall'emesso: si
                            dicono accanto, col loro totale. */}
                        {riepilogo.note_credito > 0 && (
                            <span className="fat-riquadro-nota">
                                e {riepilogo.note_credito === 1 ? '1 nota' : `${riepilogo.note_credito} note`} di
                                credito per <span className="rm-numeri">{euro(riepilogo.note_credito_totale)}</span>
                            </span>
                        )}
                    </div>
                    <div className="fat-riquadro">
                        <span className="fat-riquadro-nome">Incassato</span>
                        <span className="fat-riquadro-valore rm-numeri">{euro(riepilogo.incassato)}</span>
                    </div>
                    <div className="fat-riquadro">
                        <span className="fat-riquadro-nome">Da incassare</span>
                        <span className="fat-riquadro-valore rm-numeri">{euro(riepilogo.da_incassare)}</span>
                    </div>
                </section>
            )}

            <div className="fat-viste" role="group" aria-label="Filtri" data-non-trattiene>
                {VISTE.map((v) => {
                    const attiva = filtroScelto === v.chiave;
                    return (
                        <button key={v.chiave || 'tutte'} type="button" data-vista={v.chiave || 'tutte'}
                                className={`fat-vista${attiva ? ' fat-vista--attiva' : ''}`}
                                aria-pressed={attiva} title={v.titolo}
                                onClick={() => scegli('filtro', v.chiave)}>
                            {v.etichetta}
                            <span className="fat-vista-numero rm-numeri">
                                {conteggi[v.conteggio] != null ? conteggi[v.conteggio] : '…'}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Il numero accanto alla voce del menu conta i documenti da
                correggere di tutti i mesi: chi arriva dalla voce (il mese di
                oggi) deve sapere che ce ne sono anche fuori dal mese che
                guarda. Il numero stesso porta a «tutti i mesi», dove questa
                riga non serve (utils/fattureDaCorreggere.js). */}
            {conPeriodo && fuori > 0 && (
                <p className="fat-fuori" role="status">
                    {fuori === 1 ? '1 documento da correggere è' : `${fuori} documenti da correggere sono`} di
                    altri mesi.{' '}
                    <button type="button" className="fat-collegamento"
                            onClick={() => setParametri((p) => {
                                const n = new URLSearchParams(p);
                                n.delete('trimestre');
                                n.set('mese', MESE_TUTTI);
                                n.set('filtro', FILTRO_DA_CORREGGERE);
                                return n;
                            }, { replace: true })}>
                        Mostrali
                    </button>
                </p>
            )}

            {caricamento && !elenco && <p className="fat-vuoto">Caricamento…</p>}

            {/* UNA PAGINA VUOTA DI UN ELENCO CHE NON LO E' si dice per
                quello che e', col modo di tornare: «Nessun documento» sopra
                «8 documenti» si contraddiceva. Succede se l'elenco si
                accorcia mentre lo si guarda (una bozza tolta da un'altra
                postazione). */}
            {elenco && !elenco.fatture.length && elenco.totale > 0 && (
                <p className="fat-vuoto" role="status">
                    La pagina <span className="rm-numeri">{elenco.pagina}</span> è oltre la fine
                    dell'elenco: i documenti sono <span className="rm-numeri">{elenco.totale}</span>, in{' '}
                    {elenco.pagine === 1 ? 'una pagina' : <><span className="rm-numeri">{elenco.pagine}</span> pagine</>}.{' '}
                    <button type="button" className="fat-collegamento" onClick={() => vaiAPagina(1)}>
                        Torna alla prima pagina
                    </button>
                </p>
            )}

            {elenco && !elenco.fatture.length && !elenco.totale && (
                <p className="fat-vuoto">
                    Nessun documento con questi filtri
                    {conPeriodo && periodoPerEsteso ? ` ${periodoPerEsteso}` : ''}.
                    {conPeriodo && (
                        <>
                            {' '}
                            <button type="button" className="fat-collegamento"
                                    onClick={() => scegliPeriodo(MESE_TUTTI)}>
                                Cerca in tutti i mesi
                            </button>
                        </>
                    )}
                </p>
            )}

            {elenco && elenco.fatture.length > 0 && (
                <div className="fat-elenco rm-scorre">
                <table className="fat-tabella">
                    <thead>
                        <tr>
                            <th>Numero</th>
                            <th>Data</th>
                            <th>Intestatario</th>
                            <th className="destra">Totale</th>
                            <th className="destra">Bollo</th>
                            <th>SdI</th>
                            <th>Sistema TS</th>
                            <th>Incasso</th>
                            <th aria-label="Azioni" />
                        </tr>
                    </thead>
                    <tbody>
                        {elenco.fatture.map((f) => {
                            const ts = TS[f.ts_stato];
                            const daRifare = f.stato === 'emessa' && DA_RIGENERARE.includes(f.fe_stato);
                            const scartata = f.fe_stato === 'scartata';
                            /* Lo scarto corretto e non ancora chiuso (Alex,
                               domanda 30 del 15/10/2026, migration 335: «dopo
                               "Correggi e rifai l'XML" lo stato diventa
                               "Corretta, da trasmettere" e la fattura resta nel
                               filtro "Da correggere"; esce solo con "Segnata
                               come trasmessa" - a mano, con chi e quando - o,
                               con Aruba, con l'accettazione dello SdI»; e il
                               19/10/2026, nella chat del beta, «Testo e riga
                               aperta»: la riga dello scarto resta aperta fino a
                               «Segna come trasmessa»). Lo stato lo dice il
                               server (fe_dopo_scarto, dalla funzione del
                               database): qui nessuna copia della regola. */
                            const corretta = f.stato === 'emessa' && !daRifare && STATI_DOPO_SCARTO[f.fe_dopo_scarto] ? f.fe_dopo_scarto : null;
                            const segnabile = corretta === 'da_trasmettere' && f.fe_invio !== 'aruba';
                            const genere = [
                                f.tipo_documento === 'nota_credito' ? 'Nota di credito' : null,
                                f.soggetto_tipo === 'azienda' ? 'Azienda' : (f.soggetto_con_piva ? 'Partita IVA' : 'Privato'),
                            ].filter(Boolean).join(' · ');
                            return (
                                <React.Fragment key={f.id}>
                                <tr className={daRifare ? 'fat-riga-da-rifare' : (corretta ? 'fat-riga-corretta' : undefined)} data-documento={f.id}>
                                    <td className="fat-numero">
                                        {f.numero
                                            ? <b className="rm-numeri">{f.numero}/{f.anno}</b>
                                            : <span className="rm-stato fat-bozza">Bozza</span>}
                                        {f.annullata_il && <span className="rm-stato rm-stato-errore">annullata</span>}
                                    </td>
                                    <td className="fat-data rm-numeri">{dataDoc(f.data_documento, !conPeriodo)}</td>
                                    <td className="fat-intestatario-cella">
                                        <span className="fat-intestatario">{f.intestatario}</span>
                                        <span className="fat-genere">{genere}</span>
                                        {/* Il codice al Sistema TS corretto dopo
                                            l'emissione (Alex, 4/10/2026): il
                                            documento porta ancora quello vecchio. */}
                                        <AvvisoCfVecchio fattura={f} />
                                    </td>
                                    <td className="destra rm-numeri">{importo(f.totale)}</td>
                                    <td className="destra rm-numeri fat-tenue">
                                        {Number(f.bollo) > 0 ? importo(f.bollo) : '—'}
                                    </td>
                                    {/* La fattura elettronica (blocco 6): lo stato
                                        e l'XML da scaricare. Il motivo di un «da
                                        correggere» e lo scarto stanno nella riga
                                        aperta qui sotto, col gesto per rifarlo. */}
                                    <td className="fat-sdi-cella"><StatoSdi fattura={f} compatto rigenera={false} segna={false} onCambiata={carica} /></td>
                                    <td>
                                        {ts
                                            ? <span className={`fat-ts ${ts.classe}`} title={ts.titolo}>{ts.testo}</span>
                                            : <span className="fat-ts-no">—</span>}
                                    </td>
                                    <td className="fat-incasso-cella">
                                        {f.incasso_stato === 'incassata' && <span className="fat-incassata">Incassata</span>}
                                        {f.incasso_stato === 'da_incassare' && 'Da incassare'}
                                        {f.incasso_stato === 'acconto' && (
                                            <>
                                                Acconto
                                                <em className="fat-acconto">
                                                    incassati <span className="rm-numeri">{importo(f.incassato)}</span>
                                                </em>
                                            </>
                                        )}
                                        {!f.incasso_stato && <span className="fat-ts-no">—</span>}
                                    </td>
                                    <td className="fat-azioni">
                                        {f.stato === 'bozza' ? (
                                            <>
                                                <button title="Riprendi"
                                                        onClick={() => riprendi(f)}>
                                                    <Pencil size={15} />
                                                </button>
                                                <button title="Elimina la bozza"
                                                        className="pericolo"
                                                        onClick={() => eliminaBozza(f)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button title="Stampa"
                                                        onClick={() => scaricaPdf(f)}>
                                                    <Printer size={15} />
                                                </button>
                                                {f.tipo_documento === 'fattura'
                                                    && f.stato === 'emessa'
                                                    && !f.annullata_il && (
                                                    <button title="Storna con nota di credito"
                                                            onClick={() => apriStorno(f)}>
                                                        <RotateCcw size={15} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                {/* LO SCARTO APERTO NELLA RIGA (mockup «Fatture»):
                                    il codice e la descrizione che lo SdI ha
                                    mandato, o il motivo per cui l'XML non ha
                                    passato i controlli, da quando, e il gesto
                                    per rifarlo. «Vedi XML» solo per lo scarto:
                                    un XML che non passa i controlli non esce. */}
                                {daRifare && (
                                    <tr className="fat-scarto-riga">
                                        <td colSpan={9}>
                                            <div className="fat-scarto" data-scarto={f.id}>
                                                <span className="fat-scarto-codice rm-numeri">
                                                    {scartata
                                                        ? `SCARTO${f.fe_scarto?.codice ? ` ${f.fe_scarto.codice}` : ''}`
                                                        : 'XML DA CORREGGERE'}
                                                </span>
                                                <span className="fat-scarto-testo">
                                                    <span className="fat-scarto-motivo">
                                                        {(scartata ? f.fe_scarto?.descrizione : f.fe_motivo)
                                                            || (scartata ? 'Lo SdI non ha indicato il motivo.' : 'Il motivo non è stato registrato.')}
                                                    </span>
                                                    {f.fe_stato_dal && (
                                                        <span className="fat-scarto-quando">
                                                            {scartata ? 'notifica ricevuta il ' : 'dal '}
                                                            <span className="rm-numeri">{momento(f.fe_stato_dal)}</span>
                                                        </span>
                                                    )}
                                                </span>
                                                {scartata && (
                                                    <button type="button" className="fat-btn" onClick={() => vediXml(f)}>
                                                        Vedi XML
                                                    </button>
                                                )}
                                                <button type="button" className="fat-btn scuro"
                                                        disabled={rifacendo === f.id}
                                                        title="Rifà l'XML dopo aver corretto quello che il motivo dice"
                                                        onClick={() => rifaiXml(f)}>
                                                    {scartata ? 'Correggi e rifai l\'XML' : 'Rigenera l\'XML'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {/* LO SCARTO CORRETTO, ANCORA APERTO (domanda 30,
                                    migration 335): la stessa riga, finche' la
                                    fattura non e' segnata come trasmessa o lo SdI
                                    non l'accetta. Il gesto sta qui, non nella
                                    cella SdI. */}
                                {corretta && (
                                    <tr className="fat-scarto-riga fat-scarto-riga--corretta">
                                        <td colSpan={9}>
                                            <div className="fat-scarto fat-scarto--corretta" data-scarto={f.id}>
                                                <span className="fat-scarto-codice rm-numeri">SCARTO CORRETTO</span>
                                                <span className="fat-scarto-testo">
                                                    <span className="fat-scarto-motivo">
                                                        {corretta === 'attende_esito'
                                                            ? 'L\'XML rifatto è partito: la riga si chiude con l\'esito dello SdI.'
                                                            : segnabile
                                                                ? 'L\'XML rifatto è pronto: caricalo sul portale Fatture e Corrispettivi, poi segna la fattura come trasmessa.'
                                                                : 'L\'XML rifatto è pronto: la riga si chiude quando lo SdI lo accetta.'}
                                                    </span>
                                                </span>
                                                {segnabile && (
                                                    <button type="button" className="fat-btn scuro"
                                                            disabled={segnando === f.id}
                                                            title="Dopo averla caricata sul portale: la fattura esce da quelle da correggere"
                                                            onClick={() => segnaTrasmessa(f)}>
                                                        Segna come trasmessa
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}

            {elenco && (
                <div className="fat-conteggio">
                    <span>
                        <span className="rm-numeri">{elenco.totale}</span>
                        {` document${elenco.totale === 1 ? 'o' : 'i'}`}
                    </span>
                    {elenco.pagine > 1 && (
                        <span className="fat-pagine">
                            <button disabled={pagina <= 1} aria-label="Pagina precedente"
                                    onClick={() => vaiAPagina(pagina - 1)}>
                                <ChevronLeft size={15} />
                            </button>
                            pagina {elenco.pagina} di {elenco.pagine}
                            <button disabled={pagina >= elenco.pagine} aria-label="Pagina successiva"
                                    onClick={() => vaiAPagina(pagina + 1)}>
                                <ChevronRight size={15} />
                            </button>
                        </span>
                    )}
                </div>
            )}

            {nuova && (
                <NuovoDocumento
                    onChiudi={() => setNuova(false)}
                    onFatto={() => { setNuova(false); carica(); }}
                    onErrore={setErrore}
                    onTolta={carica}
                />
            )}

            {storno && (
                <ModaleStorno
                    documento={storno}
                    onChiudi={() => setStorno(null)}
                    onFatto={() => { setStorno(null); carica(); }}
                />
            )}

            {ripresa && (
                <div className="fat-modale-fondo" onClick={chiudiRipresa}>
                    <div className="fat-modale grande" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <h3>
                                Bozza per {ripresa.denominazione
                                    || `${ripresa.cognome || ''} ${ripresa.nome || ''}`.trim()
                                    || 'documento'}
                            </h3>
                            <button onClick={chiudiRipresa}><X size={18} /></button>
                        </header>
                        <RigheDocumento
                            bozza={ripresa}
                            onAggiorna={setRipresa}
                            onFatto={() => { apriDocumento(null); carica(); }}
                            onErrore={setErrore}
                            onTolta={carica}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FatturazioneConsole;
